import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '../button';
import CustomSingleSelect from '../custom_single_select';
import RequiredReasonTextarea from '../required_reason_textarea';
import { trim_reason } from '../required_reason_textarea/reason_rules';
import { save_care_request } from './care_request_api';
import {
  INTERNAL_NOTE_LABEL,
  INTERNAL_NOTE_PLACEHOLDER,
  KIND_TEACHER_CHANGE,
  care_api_error,
  covers_care,
  has_errors,
  new_care_draft
} from './care_request_rules';
import {
  STAGE_UPLOAD,
  change_teacher_with_care,
  swap_error_message,
  validate_teacher_change
} from './teacher_change_flow';
import { CareRequestFields, CareSaveRetry, TeacherNoteField } from './index';

// PROD-7828 follow-up: the group class / workshop "Change teacher" form with Care's teacher request time, the internal
// note and the required note for the teacher.
// The host owns the apis call (swap) and what happens after (on_done: close + reload; on_error: notify).
function TeacherChangeForm(props) {
  const {
    session_uuid,
    session_type,
    teacher_options,
    initial_teacher_uuid,
    swap,
    on_done,
    on_error
  } = props;
  const [teacher_uuid, setTeacher] = useState(initial_teacher_uuid || '');
  const [reason, setReason] = useState('');
  const [draft, setDraft] = useState(() =>
    covers_care(KIND_TEACHER_CHANGE, session_type)
      ? new_care_draft(KIND_TEACHER_CHANGE, Date.now())
      : null
  );
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(null); // {payload, message, busy}
  const screenshot_key = useRef('');
  const in_flight = useRef(false); // closes the double-click window before setState lands

  const submit = async () => {
    const errs = validate_teacher_change({ teacher_uuid, reason, draft, now_ms: Date.now() });
    if (has_errors(errs)) {
      setErrors(errs);
      return;
    }
    if (in_flight.current) return;
    in_flight.current = true;
    setBusy(true);
    const note = trim_reason(reason);
    try {
      const res = await change_teacher_with_care(
        { session_uuid, teacher_uuid, note, draft, screenshot_key: screenshot_key.current },
        { swap: () => swap(teacher_uuid, note) }
      );
      if (res.saved) {
        on_done();
        return;
      }
      setRetry({
        payload: res.payload,
        busy: false,
        message: care_api_error(res.error, 'Try again.').message
      });
    } catch (e) {
      if (e.stage === STAGE_UPLOAD) {
        on_error(
          care_api_error(e.cause, 'The screenshot could not be uploaded. Nothing was changed.')
            .message
        );
      } else {
        screenshot_key.current = e.screenshot_key || '';
        on_error(swap_error_message(e.cause));
      }
    } finally {
      in_flight.current = false;
      setBusy(false);
    }
  };

  const retry_save = () => {
    setRetry({ ...retry, busy: true });
    save_care_request(retry.payload)
      .then(() => on_done())
      .catch((e) =>
        setRetry({ ...retry, busy: false, message: care_api_error(e, 'Try again.').message })
      );
  };

  return (
    <div>
      <h2>Change teacher</h2>
      <CustomSingleSelect
        label="Select a teacher"
        options={teacher_options}
        onChange={(e) => {
          setTeacher(e.target.value);
          setErrors({});
        }}
        value={teacher_uuid}
        style={{ width: '300px' }}
      />
      {errors.teacher ? <p style={{ color: 'var(--state-error)' }}>* {errors.teacher}</p> : null}
      <RequiredReasonTextarea
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
          setErrors({});
        }}
        label={INTERNAL_NOTE_LABEL}
        placeholder={INTERNAL_NOTE_PLACEHOLDER}
        error={!!errors.reason}
        errorText={errors.reason}
        height="60px"
      />
      {draft ? (
        <TeacherNoteField
          value={draft.teacher_note || ''}
          onChange={(teacher_note) => {
            setDraft({ ...draft, teacher_note });
            setErrors({});
          }}
          error={errors.teacher_note}
        />
      ) : null}
      {draft ? (
        <CareRequestFields
          draft={draft}
          errors={errors}
          onChange={(patch) => {
            setDraft({ ...draft, ...patch });
            setErrors({});
            if ('file' in patch) screenshot_key.current = ''; // a new file must be uploaded again
          }}
          initiatedByFixed="TEACHER"
          requestedLabel="Teacher request time (IST)"
        />
      ) : null}
      {retry ? (
        <CareSaveRetry
          message={retry.message}
          busy={retry.busy}
          onRetry={retry_save}
          onClose={on_done}
        />
      ) : (
        <Button onClick={submit} disabled={busy}>
          {busy ? 'Submitting…' : 'Change'}
        </Button>
      )}
    </div>
  );
}

TeacherChangeForm.propTypes = {
  session_uuid: PropTypes.string.isRequired,
  session_type: PropTypes.string,
  teacher_options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  initial_teacher_uuid: PropTypes.string,
  swap: PropTypes.func.isRequired,
  on_done: PropTypes.func.isRequired,
  on_error: PropTypes.func.isRequired
};

TeacherChangeForm.defaultProps = {
  session_type: '',
  initial_teacher_uuid: ''
};

export default TeacherChangeForm;
